import {
    Bundle,
    BundleStamp,
    BundleStampFlavor,
    BundleStub,
    ensureBundleById,
    ensureFullAllTransfersForBundle,
    ensureStampsForBundle,
    KingBasura,
    SortOrder,
    Transfer,
    useBundleById,
    useFullAllTransfersForBundle,
    useStampsForBundle
} from "../rpc/webrpc-client.gen.ts";
import {useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {TruckCapsule} from "../truck/capsule.tsx";
import {StampSceneView, StampTimelineTileContent} from "../stamp/stamps.tsx";
import {BaseTimelineScene, cmpScenesNewestFirst, cmpTimeCoords, pointillize} from "../timeline/timeline.tsx";
import {QueryClient, queryOptions, skipToken, useQueryClient} from "@tanstack/react-query";
import {Box, BoxProps, SheetProps, Stack} from "@mui/joy";
import {SuspenseDialog} from "../components/SuspenseDialog.tsx";
import {BundleCapsule} from "./capsule.tsx";
import {faMessageCheck} from "@fortawesome/pro-regular-svg-icons";
import {z} from "zod/v4";
import {
    synthTransferScenes,
    TransferScene,
    TransferSceneTileContent,
    useTransferSceneColor
} from "../transit/transfer.tsx";
import {Flexidate, spawnTstzSpan} from "../tstzspan.ts";
import {DateArg, isAfter} from "date-fns";
import {StampFlavorIconMap} from "../stamp/stampFlavorIconMap.tsx";
import {SceneViewProps} from "../timeline/sceneViewProps.tsx";
import {DiarySceneView, DiaryView} from "../timeline/diary.tsx";
import {TieredOrActive} from "../dispatch/tieredOrActive.tsx";
import {BundleStatus} from "./status.tsx";
import {useRefreshableInfallibleQuery} from "../util/tqrefresh.tsx";
import {cleanse, cmpById, Comparator} from "../util/arutil.tsx";
import {InfallibleUseQueryResult} from "../util/useInfallibleQuery.tsx";
import {useMothership} from "../tq/tqx.tsx";

const BundleSceneBase = z.object({
    ...BaseTimelineScene.shape,
    subjectFlavor: z.literal("bundle"),
    subject: BundleStub,
});

export const BundleStampScene = z.object({
    ...BundleSceneBase.shape,
    sceneFlavor: z.literal("stamp"),
    stamp: BundleStamp,
});
export type BundleStampScene = z.infer<typeof BundleStampScene>;

export const BundlePOVTransferScene = z.object({
    ...BundleSceneBase.shape,
    sceneFlavor: z.literal("transfer"),
    transferScene: TransferScene,
});
export type BundlePOVTransferScene = z.infer<typeof BundlePOVTransferScene>;

function rebadgeTransferScene(from: TransferScene): BundlePOVTransferScene {
    return {
        subjectFlavor: "bundle",
        subject: from.subject.bundle,
        sceneFlavor: "transfer",
        transferScene: from,
        where: from.where,
        when: from.when,
    };
}

export const BundleScene
    = z.discriminatedUnion("sceneFlavor", [
    BundleStampScene, BundlePOVTransferScene
]);
export type BundleScene = z.infer<typeof BundleScene>;

function useLabelForBundleTransferScene(scene?: TransferScene) {
    const {t} = useTranslation();
    if (!scene) {
        return undefined;
    }

    if (scene.direction === "PICKUP") {
        if (scene.sceneFlavor === "start") {
            return t("driver.onloadStarted");
        } else {
            return t("driver.onloadEnded");
        }
    } else {
        if (scene.sceneFlavor === "start") {
            return t("driver.offloadStarted");
        } else {
            return t("driver.offloadEnded");
        }
    }
}

export function BundleTransferSceneView({scene, ...rest}: Omit<SceneViewProps<BundlePOVTransferScene>, "children">) {
    const label = useLabelForBundleTransferScene(scene.transferScene);

    return <DiarySceneView scene={scene} {...rest}>
        <Stack direction="row" spacing={2} sx={{alignItems: "center"}}>
            <TruckCapsule truck={scene.transferScene.subject.truck}/>
            <Box sx={{fontSize: "lg"}}>{label}</Box>
        </Stack>
    </DiarySceneView>;
}

function BundleSceneView({scene, color: inColor, ...rest}: Omit<SceneViewProps<BundleScene>, "children">) {
    if (scene.sceneFlavor === "stamp") {
        let color = inColor;
        if (scene.stamp.flavor === BundleStampFlavor.enum.FIELD_COMPLETE_ACCEPT
            || scene.stamp.flavor === BundleStampFlavor.enum.DELIVERED) {
            color = "success";
        }
        return <StampSceneView scene={scene} color={color} iconMap={iconsByBundleStampFlavor} {...rest}/>;
    } else {
        return <BundleTransferSceneView scene={scene} {...rest}/>
    }
}

// function BundleOnloadedStampCapsule(args ?: { stamp: BundleStamp }) {
//     const {t} = useTranslation();
//     const stamp = args?.stamp;
//     const transferTqrv = useLastTransferForBundleBefore(stamp
//         ? {bundleId: stamp.subjectId, before: stamp.posted}
//         : undefined);
//     const xfer = transferTqrv.data;
//
//     if (!xfer?.truck) {
//         return null;
//     } else {
//         return <Stack direction="row" spacing={2} sx={{alignItems: "center"}}>
//             <Box sx={{fontSize: "lg"}}>{t("onloaded")}</Box>
//             <TruckCapsule truck={xfer.truck}/>
//         </Stack>
//     }
// }

export const iconsByBundleStampFlavor: StampFlavorIconMap<"bundle"> = {
    [BundleStampFlavor.enum.FIELD_COMPLETE_ACCEPT]: {icon: faMessageCheck},
} as const;

// function useBundleStampTimelineScenes({bundleId, ...rest}: LimitedUseQueryOptions<{
//     bundleId: string
// }>): LimitedUseQueryResult<TimelineSceneViewProps[]> {
//     const {t} = useTranslation();
//     const mama = useMothership();
//     const {data: stampsData}
//         = useRefreshableQuery({...watchStampsForBundleTqo(mama, {bundleId}), ...rest});
//     const stamps = stampsData?.stamps;
//     const rv: LimitedUseQueryResult<TimelineSceneViewProps[]> = useMemo(() => {
//         if (!stamps) {
//             return MADA;
//         }
//         return {
//             data: stamps.map((stamp) => {
//                 switch (stamp.flavor) {
//                     case BundleStampFlavor.FIELD_COMPLETE_PROPOSE:
//                         return {
//                             when: {at: stamp.posted},
//                             what: <StampTimelineEntry
//                                 stamp={stamp} color="neutral" variant="soft"
//                                 extraChildren={<BundleOnloadedStampCapsule stamp={stamp}/>}/>
//                         };
//                     case BundleStampFlavor.FIELD_COMPLETE_ACCEPT:
//                         return {
//                             when: {at: stamp.posted},
//                             what: <StampTimelineEntry stamp={stamp} color="success" variant="soft"
//                                                       extraChildren={t("fieldApproved")}/>,
//                             color: "success", variant: "soft",
//                         }
//                         break;
//                     case BundleStampFlavor.DELIVERED:
//                         return {
//                             when: {at: stamp.posted},
//                             what: <StampTimelineEntry stamp={stamp} color="success" variant="soft"
//                                                       extraChildren={t("delivered")}/>,
//                             color: "success", variant: "soft",
//                         }
//                         break;
//                     case undefined:
//                         return {
//                             when: {at: stamp.posted},
//                             what: <StampTimelineEntry stamp={stamp}/>
//                         };
//                         break;
//                     default:
//                         return {
//                             when: {at: stamp.posted},
//                             what: <StampTimelineEntry
//                                 stamp={stamp} color="danger" variant="solid"
//                                 extraChildren={JSON.stringify(stamp)}/>
//                         };
//                 }
//             }), isSuccess: true
//         };
//     }, [stamps, t]);
//
//     return rv;
// }

// function useBundleTimelineSceneProps({bundleId, enabled, sortOrder, ...rest}: LimitedUseQueryOptions<{
//     bundleId: string,
//     sortOrder: SortOrder,
// }>): LimitedUseQueryResult<TimelineSceneViewProps[]> {
//     const {data: stampScenes}
//         = useBundleStampTimelineScenes({bundleId, enabled, ...rest});
//     const cmp = useTimeCoordinatedComparator(sortOrder);
//
//     const rv: LimitedUseQueryResult<TimelineSceneViewProps[]> = useMemo(() => {
//         if (!stampScenes) {
//             return MADA;
//         }
//         const unsorted = [...stampScenes];
//         const sorted = unsorted.toSorted(cmp);
//         return {data: sorted, isSuccess: true};
//     }, [cmp, stampScenes]);
//
//     return rv;
// }

export const bundleTimelineVerb = "BundleTimeline";

export function bundleTimelineTqk(args?: { bundleId: string }) {
    return args ? [bundleTimelineVerb, args] as const : [bundleTimelineVerb] as const;
}

export const BundleTimelineFood = z.object({
    bundle: BundleStub,
    stamps: BundleStamp.array(),
    stampsAsof: z.optional(Flexidate),
    transfers: Transfer.array(),
    transfersAsof: z.optional(Flexidate),
});
export type BundleTimelineFood = z.infer<typeof BundleTimelineFood>;

function synthStampScenes({bundle, stamps}: { bundle: BundleStub, stamps: BundleStamp[] }): BundleStampScene[] {
    return stamps.map(stamp => ({
        subjectFlavor: "bundle",
        subject: bundle,
        sceneFlavor: "stamp",
        stamp,
        when: {at: stamp.posted},
    }));
}

function synthBundleScenes(food: BundleTimelineFood): BundleScene[] {
    const unsorted: BundleScene[] = [
        ...synthStampScenes(food),
        ...synthTransferScenes(food).map(rebadgeTransferScene),
    ];

    const sorted = unsorted
        .toSorted((a, b) =>
            cmpTimeCoords(b.when, a.when));
    return sorted;
}

export interface WatchBundleTimelineArgs {
    bundleId: string;
}

// this can go stale unless there is a relay mounted watching over it

export function ensureUnsortedBundleTimeline({tqc, mama, args}: {
    tqc: QueryClient,
    mama: KingBasura,
    args: WatchBundleTimelineArgs,
}): Promise<BundleScene[]> {
    const qa = {tqc, mama, args} as const;
    return Promise.all([
        ensureBundleById(qa),
        ensureStampsForBundle(qa),
        ensureFullAllTransfersForBundle(qa),
    ]).then(([{bundle}, {stamps}, transferRpcs]) => {
        const now = new Date();
        const transfers = transferRpcs.map(rpc => rpc.transfer);
        const maybe: Partial<BundleTimelineFood> = {
            bundle,
            stamps, stampsAsof: now,
            transfers, transfersAsof: now,
        };
        const synthFoodZrv = BundleTimelineFood.safeParse(maybe);
        if (!synthFoodZrv.success) {
            throw synthFoodZrv.error;
        }
        return synthFoodZrv.data;
    }).then(food => {
        const scenes = synthBundleScenes(food);
        return scenes;
    });
}

export function bundleTimelineTqo({tqc, mama, args}: {
    tqc: QueryClient,
    mama: KingBasura,
    args?: WatchBundleTimelineArgs
}) {
    return queryOptions({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey: bundleTimelineTqk(args),
        queryFn: args
            ? () => ensureUnsortedBundleTimeline({tqc, mama, args})
            : skipToken,
    });
}

export function useBundleTimeline(args?: WatchBundleTimelineArgs): InfallibleUseQueryResult<BundleScene[], Error> {
    const tqc = useQueryClient();
    const mama = useMothership();
    return useRefreshableInfallibleQuery(bundleTimelineTqo({tqc, mama, args}));
}

export const lastSceneForBundleVerb = "LastSceneForBundle";

export function lastSceneForBundleTqk(args ?: WatchBundleTimelineArgs) {
    return args ? [lastSceneForBundleVerb, args] as const : [lastSceneForBundleVerb] as const;
}

export function ensureLastSceneForBundle(
    args: {
        tqc: QueryClient, mama: KingBasura, args: WatchBundleTimelineArgs
    }
) {
    return ensureUnsortedBundleTimeline(args)
        .then(unsorted => unsorted.toSorted(cmpScenesNewestFirst))
        .then(sorted => sorted[0] ?? null);
}

export function lastSceneForBundleTqo(mama: KingBasura, args?: WatchBundleTimelineArgs) {
    return queryOptions({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey: lastSceneForBundleTqk(args),
        queryFn: args
            ? ({client: tqc}) => ensureLastSceneForBundle({tqc, mama, args})
            : skipToken,
    });
}

export function useLastSceneForBundle(args?: WatchBundleTimelineArgs) {
    const mama = useMothership();
    return useRefreshableInfallibleQuery(lastSceneForBundleTqo(mama, args));
}

export function BundleTimelineRelay(args: { bundleId: string }) {
    const tqc = useQueryClient();
    const bundleId = args.bundleId;

    const tqk = bundleTimelineTqk(args);
    const bundleTqrv = useBundleById(args)
    const stampTqrv = useStampsForBundle(args);
    const xferTqrv = useFullAllTransfersForBundle(args);

    let transfers: Transfer[] | undefined;
    let transfersAsof: DateArg<Date> | undefined;
    if (xferTqrv.isSuccess) {
        transfers = cleanse(xferTqrv.data);
        transfersAsof = xferTqrv.dataUpdatedAt;
    }

    const synthFoodTqrv = useMemo(() => {
        const maybe: Partial<BundleTimelineFood> = {
            bundle: bundleTqrv.data,
            stamps: stampTqrv.data,
            stampsAsof: stampTqrv.dataUpdatedAt,
            transfers,
            transfersAsof,
        };
        const zprv = BundleTimelineFood.safeParse(maybe);
        return zprv;
    }, [bundleTqrv.data, stampTqrv.data, stampTqrv.dataUpdatedAt, transfers, transfersAsof]);

    useEffect(() => {
        if (synthFoodTqrv.success) {
            const scenes = synthBundleScenes(synthFoodTqrv.data);
            tqc.setQueryData(tqk, scenes);
            const latestScene = scenes[0];
            if (latestScene) {
                tqc.setQueryData(lastSceneForBundleTqk(args), latestScene);
            }

        }
    }, [args, bundleId, synthFoodTqrv.data, synthFoodTqrv.success, tqc, tqk]);

    return null;
}

export const BundleActor = z.object({
    bundle: Bundle,
    lastScene: z.nullable(BundleScene),
    status: BundleStatus,
});
export type BundleActor = z.infer<typeof BundleActor>;

export function bundleActorLastModified(xb: BundleActor): DateArg<Date> {
    let rv: DateArg<Date> = xb.bundle.created;
    if (xb.bundle.asof && isAfter(xb.bundle.asof, rv)) {
        rv = xb.bundle.asof;
    }
    const validSpan = spawnTstzSpan(xb.bundle.validSpan);
    if (validSpan.lower && isAfter(validSpan.lower, rv)) {
        rv = validSpan.lower;
    }
    if (xb.lastScene) {
        const posted = pointillize(xb.lastScene.when).at;
        if (isAfter(posted, rv)) {
            rv = posted;
        }
    }
    return rv;
}

function cmpBundleActorsStableOrder(a: BundleActor, b: BundleActor) {
    return cmpById(a.bundle, b.bundle);
}

function cmpBundleActorsActiveOrder(a: BundleActor, b: BundleActor) {
    return cmpScenesNewestFirst(a.lastScene, b.lastScene);
}

export const dispatchBundleAscSorters: Record<TieredOrActive, Comparator<BundleActor>> = {
    tiered: cmpBundleActorsStableOrder,
    active: cmpBundleActorsActiveOrder,
} as const;

export interface BundleActors {
    bundleActors: BundleActor[];
}

export interface BundleStubs {
    bundleStubs: BundleStub[];
}

// export function useBundleTimeline(args: { bundleId: string }): InfallibleUseQueryResult<BundleScene[], Error> {
//     const tqc = useQueryClient();
//     const mama = useMothership();
//     // const bomb = useBomb();
//
//     const queryFn = useCallback(() => {
//         const qa = {tqc, mama, args};
//         return Promise.all([
//             ensureStampsForBundle(qa),
//             ensureFullAllTransfersForBundle(qa).then(rpc =>
//                 ({transfers: rpc.map(rpcxfer => rpcxfer.transfer)})),
//         ])
//             .then(([{stamps}, {transfers}]) => {
//                 const now = new Date();
//                 const maybe: Partial<BundleTimelineFood> = {
//                     stamps, stampsAsof: now,
//                     transfers, transfersAsof: now,
//                 };
//                 const synthFoodZrv = BundleTimelineFood.safeParse(maybe);
//                 if (!synthFoodZrv.success) {
//                     throw synthFoodZrv.error;
//                 }
//                 return synthFoodZrv.data;
//             })
//             .then(food => {
//                 const scenes = synthBundleScenes(food);
//                 return scenes;
//             });
//     }, [args, mama, tqc]);
//
//     return useInfallibleQuery(queryOptions({queryKey: bundleTimelineTqk(args), queryFn}));
// }

export interface BundleDiaryViewProps extends Omit<BoxProps, "children"> {
    bundleId: string,
    sortOrder: SortOrder
};

export function BundleDiaryView({bundleId, sortOrder, ...rest}: BundleDiaryViewProps) {
    const {t} = useTranslation();
    const bundleTqrv = useBundleById({bundleId});
    const timelineTqrv = useBundleTimeline({bundleId});
    const rawScenes = timelineTqrv.data;
    const sortedScenes = useMemo(() => {
        switch (sortOrder) {
            case "ASC":
                return rawScenes?.toReversed() ?? [];
            case "DESC":
                return rawScenes ?? [];
        }
    }, [rawScenes, sortOrder]);

    if (bundleTqrv.isPending || timelineTqrv.isPending) {
        return <SuspenseDialog message="fetching bundle"/>;
    }

    const bundle = bundleTqrv.data;

    return !!bundle && !!rawScenes
        ? <DiaryView<BundleScene> sortedScenes={sortedScenes}
                                  Entry={BundleSceneView}
                                  targetLabel={<BundleCapsule bundle={bundle}/>}
                                  {...rest}
        />
        : t("empty");
}

export function useBundleSceneColor(scene ?: BundleScene | null): SheetProps["color"] {
    const fromTransfer = useTransferSceneColor(scene?.sceneFlavor === "transfer" ? scene.transferScene : undefined);
    const rv = fromTransfer ?? "neutral";
    return rv;
}

export function BundleSceneTileContent({scene, ...rest}: SceneViewProps<BundleScene>) {
    switch (scene.sceneFlavor) {
        case "stamp":
            return <StampTimelineTileContent scene={scene} iconMap={iconsByBundleStampFlavor} {...rest}/>
        case "transfer":
            return <TransferSceneTileContent scene={scene.transferScene} {...rest}/>;
    }
}

